/**
 * CUSTOMER FIX Stage 2 / Home v1.3 W2 — browse publishes discovery location API
 * for Lokalita disclosure. Default null → header omits location chrome.
 */
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** Manual město/PSČ/oblast pick from Nominatim — discovery location only (≠ shipping). */
export type DiscoveryManualPlaceSelection = {
  readonly lat: number;
  readonly lng: number;
  readonly label: string;
};

export interface DiscoveryShellLocationApi {
  readonly nearbyActive: boolean;
  readonly geoPending: boolean;
  readonly geoErrorKey: 'denied' | 'unavailable' | null;
  readonly radiusMeters: number | null;
  /** Reverse-geocoded or manual coarse place label when location is active. */
  readonly locationLabel: string | null;
  readonly requestNearby: () => void;
  /** H05/F05 — apply Nominatim pick without GPS. */
  readonly applyManualPlace: (place: DiscoveryManualPlaceSelection) => void;
  readonly clearNearbyContext: () => void;
}

/** Display fields only — ignore callback identity to avoid shell re-render storms. */
export function discoveryShellLocationDisplayEqual(
  a: DiscoveryShellLocationApi,
  b: DiscoveryShellLocationApi,
): boolean {
  return (
    a.nearbyActive === b.nearbyActive &&
    a.geoPending === b.geoPending &&
    a.geoErrorKey === b.geoErrorKey &&
    a.radiusMeters === b.radiusMeters &&
    a.locationLabel === b.locationLabel
  );
}

const DiscoveryShellLocationValueContext =
  createContext<DiscoveryShellLocationApi | null>(null);
const DiscoveryShellLocationSetContext = createContext<
  ((api: DiscoveryShellLocationApi | null) => void) | null
>(null);

export interface DiscoveryShellLocationDisclosureControls {
  readonly locationDisclosureOpen: boolean;
  readonly openLocationDisclosure: () => void;
  readonly closeLocationDisclosure: () => void;
}

const DiscoveryShellLocationDisclosureContext =
  createContext<DiscoveryShellLocationDisclosureControls | null>(null);

export function DiscoveryShellLocationProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [discoveryLocation, setDiscoveryLocation] =
    useState<DiscoveryShellLocationApi | null>(null);
  const [locationDisclosureOpen, setLocationDisclosureOpen] = useState(false);

  const openLocationDisclosure = useCallback((): void => {
    setLocationDisclosureOpen(true);
  }, []);

  const closeLocationDisclosure = useCallback((): void => {
    setLocationDisclosureOpen(false);
  }, []);

  const setDiscoveryLocationWithDisclosureReset = useCallback(
    (api: DiscoveryShellLocationApi | null): void => {
      if (api === null) {
        setDiscoveryLocation(null);
        setLocationDisclosureOpen(false);
        return;
      }
      setDiscoveryLocation((prev) => {
        if (prev !== null && discoveryShellLocationDisplayEqual(prev, api)) {
          // G9 — production stabilizes callbacks via useMemo + replaceParams ref;
          // skip context churn when only display fields are unchanged.
          return prev;
        }
        return api;
      });
    },
    [],
  );

  const disclosureControls = useMemo(
    (): DiscoveryShellLocationDisclosureControls => ({
      locationDisclosureOpen,
      openLocationDisclosure,
      closeLocationDisclosure,
    }),
    [closeLocationDisclosure, locationDisclosureOpen, openLocationDisclosure],
  );

  return (
    <DiscoveryShellLocationSetContext.Provider value={setDiscoveryLocationWithDisclosureReset}>
      <DiscoveryShellLocationValueContext.Provider value={discoveryLocation}>
        <DiscoveryShellLocationDisclosureContext.Provider value={disclosureControls}>
          {children}
        </DiscoveryShellLocationDisclosureContext.Provider>
      </DiscoveryShellLocationValueContext.Provider>
    </DiscoveryShellLocationSetContext.Provider>
  );
}

/** Null when not on `/browse` or BrowsePlaceholderPage has not registered. */
export function useDiscoveryShellLocation(): DiscoveryShellLocationApi | null {
  return useContext(DiscoveryShellLocationValueContext);
}

const DISCOVERY_SHELL_LOCATION_DISCLOSURE_DEFAULT: DiscoveryShellLocationDisclosureControls =
  {
    locationDisclosureOpen: false,
    openLocationDisclosure: () => undefined,
    closeLocationDisclosure: () => undefined,
  };

/** Location disclosure sheet open/close — owned by DiscoveryShellLocationProvider. */
export function useDiscoveryShellLocationDisclosure(): DiscoveryShellLocationDisclosureControls {
  const controls = useContext(DiscoveryShellLocationDisclosureContext);
  return controls ?? DISCOVERY_SHELL_LOCATION_DISCLOSURE_DEFAULT;
}

/**
 * Publish browse nearby/geo disclosure API while BrowsePlaceholderPage is mounted.
 * Clears on unmount so shell reverts when leaving browse.
 */
export function useRegisterDiscoveryShellLocation(
  api: DiscoveryShellLocationApi,
): void {
  const setDiscoveryLocation = useContext(DiscoveryShellLocationSetContext);
  const apiRef = useRef(api);

  // G5/G17 — sync latest browse callbacks each commit; never key on whole [api].
  useLayoutEffect(() => {
    apiRef.current = api;
  });

  const requestNearbyStable = useCallback((): void => {
    apiRef.current.requestNearby();
  }, []);

  const applyManualPlaceStable = useCallback(
    (place: DiscoveryManualPlaceSelection): void => {
      apiRef.current.applyManualPlace(place);
    },
    [],
  );

  const clearNearbyContextStable = useCallback((): void => {
    apiRef.current.clearNearbyContext();
  }, []);

  const stableRegistration = useMemo(
    (): DiscoveryShellLocationApi => ({
      nearbyActive: api.nearbyActive,
      geoPending: api.geoPending,
      geoErrorKey: api.geoErrorKey,
      radiusMeters: api.radiusMeters,
      locationLabel: api.locationLabel,
      requestNearby: requestNearbyStable,
      applyManualPlace: applyManualPlaceStable,
      clearNearbyContext: clearNearbyContextStable,
    }),
    [
      api.geoErrorKey,
      api.geoPending,
      api.locationLabel,
      api.nearbyActive,
      api.radiusMeters,
      applyManualPlaceStable,
      clearNearbyContextStable,
      requestNearbyStable,
    ],
  );

  const publishedRef = useRef<DiscoveryShellLocationApi | null>(null);

  useLayoutEffect(() => {
    if (setDiscoveryLocation === null) {
      return;
    }
    // G6 — bail out before setter when display fields match last publish (geoPending oscillation / churn).
    const prev = publishedRef.current;
    if (
      prev !== null &&
      discoveryShellLocationDisplayEqual(prev, stableRegistration)
    ) {
      return;
    }
    publishedRef.current = stableRegistration;
    setDiscoveryLocation(stableRegistration);
  }, [setDiscoveryLocation, stableRegistration]);

  useLayoutEffect(() => {
    if (setDiscoveryLocation === null) {
      return;
    }
    return () => {
      publishedRef.current = null;
      setDiscoveryLocation(null);
    };
  }, [setDiscoveryLocation]);
}

/** Test / rare callers that need both — prefer the hooks above. */
export function useDiscoveryShellLocationControls(): {
  readonly discoveryLocation: DiscoveryShellLocationApi | null;
  readonly setDiscoveryLocation:
    | ((api: DiscoveryShellLocationApi | null) => void)
    | null;
} {
  const discoveryLocation = useContext(DiscoveryShellLocationValueContext);
  const setDiscoveryLocation = useContext(DiscoveryShellLocationSetContext);
  return useMemo(
    () => ({ discoveryLocation, setDiscoveryLocation }),
    [discoveryLocation, setDiscoveryLocation],
  );
}
